// Metadata for every analysis methodology on the Data Analysis workbench.
// `params` describes the inputs AnalysisParamsForm needs to render for each type.
// Variable-role params use `role: 'numeric' | 'categorical' | 'any'` to filter
// which questions are offered, and `multiple: true/false`.

const analysisTypes = [
  {
    id: "descriptive",
    label: "Descriptive Statistics",
    category: "Describe",
    description: "Mean, median, mode, spread and shape for one or more numeric variables.",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true },
    ],
  },
  {
    id: "reliability",
    label: "Reliability (Cronbach's Alpha)",
    category: "Describe",
    description: "Internal-consistency check for a multi-item scale/battery.",
    params: [
      { name: "variables", label: "Scale items", role: "numeric", multiple: true, required: true, min: 2 },
    ],
  },
  {
    id: "ttest",
    label: "Independent T-Test",
    category: "Compare groups",
    description: "Compare the mean of a numeric variable across 2 groups.",
    params: [
      { name: "dv", label: "Measure (numeric)", role: "numeric", multiple: false, required: true },
      { name: "groupVar", label: "Group by (categorical, 2 groups)", role: "categorical", multiple: false, required: true },
    ],
  },
  {
    id: "anova",
    label: "One-Way ANOVA",
    category: "Compare groups",
    description: "Compare the mean of a numeric variable across 3+ groups, with Tukey post-hoc.",
    params: [
      { name: "dv", label: "Measure (numeric)", role: "numeric", multiple: false, required: true },
      { name: "groupVar", label: "Group by (categorical, 3+ groups)", role: "categorical", multiple: false, required: true },
    ],
  },
  {
    id: "chisquare",
    label: "Chi-Square Test",
    category: "Compare groups",
    description: "Test whether two categorical variables are associated.",
    params: [
      { name: "var1", label: "Variable 1", role: "categorical", multiple: false, required: true },
      { name: "var2", label: "Variable 2", role: "categorical", multiple: false, required: true },
    ],
  },
  {
    id: "correlation",
    label: "Correlation Matrix",
    category: "Relationships",
    description: "Pearson/Spearman correlation with p-values and a heatmap.",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true, min: 2 },
      {
        name: "method", label: "Method", type: "select", default: "pearson",
        options: [{ value: "pearson", label: "Pearson" }, { value: "spearman", label: "Spearman" }],
      },
    ],
  },
  {
    id: "regression",
    label: "Multiple Linear Regression",
    category: "Relationships",
    description: "Predict a numeric outcome and rank the strongest drivers.",
    params: [
      { name: "dv", label: "Dependent variable (numeric)", role: "numeric", multiple: false, required: true },
      { name: "ivs", label: "Predictors (numeric)", role: "numeric", multiple: true, required: true },
    ],
  },
  {
    id: "logistic",
    label: "Logistic Regression",
    category: "Relationships",
    description: "Predict a binary outcome; reports odds ratios, accuracy and AUC.",
    params: [
      { name: "dv", label: "Outcome (binary)", role: "categorical", multiple: false, required: true },
      { name: "ivs", label: "Predictors (numeric)", role: "numeric", multiple: true, required: true },
    ],
  },
  {
    id: "cluster",
    label: "K-Means Clustering",
    category: "Segment",
    description: "Group respondents into segments based on numeric variables.",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true, min: 2 },
      { name: "k", label: "Number of segments (blank = auto)", type: "number", optional: true },
    ],
  },
  {
    id: "pca",
    label: "Principal Component Analysis",
    category: "Segment",
    description: "Reduce many numeric variables down to their main underlying dimensions.",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true, min: 3 },
      { name: "nComponents", label: "Components to show", type: "number", default: 2 },
    ],
  },
  {
    id: "turf",
    label: "TURF (Reach & Frequency)",
    category: "Segment",
    description: "Find the combination of items that reaches the most respondents.",
    params: [
      { name: "variables", label: "Reach items (binary)", role: "categorical", multiple: true, required: true, min: 2 },
      { name: "nItems", label: "Combination size", type: "number", default: 3 },
    ],
  },
];

export const CATEGORIES = ["Describe", "Compare groups", "Relationships", "Segment"];

export default analysisTypes;
